import { CheckIcon, CopyIcon } from "lucide-react";
import {
	type ComponentProps,
	createContext,
	type HTMLAttributes,
	useContext,
	useEffect,
	useState,
} from "react";
import { type BundledLanguage, codeToHtml, type ShikiTransformer } from "shiki";
import { type CodeThemePair, DEFAULT_CODE_THEMES, useCodeThemes } from "../../lib/appearance/code-themes";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";

type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
	code: string;
	language: BundledLanguage;
	showLineNumbers?: boolean;
};

type CodeBlockContextType = {
	code: string;
};

const CodeBlockContext = createContext<CodeBlockContextType>({
	code: "",
});

const lineNumberTransformer: ShikiTransformer = {
	name: "line-numbers",
	line(node, line) {
		node.children.unshift({
			type: "element",
			tagName: "span",
			properties: {
				className: [
					"inline-block",
					"min-w-10",
					"mr-4",
					"text-right",
					"select-none",
					"text-muted-foreground",
				],
			},
			children: [{ type: "text", value: String(line) }],
		});
	},
};

export async function highlightCode(
	code: string,
	language: BundledLanguage,
	showLineNumbers = false,
	themes: CodeThemePair = DEFAULT_CODE_THEMES,
) {
	const transformers: ShikiTransformer[] = showLineNumbers
		? [lineNumberTransformer]
		: [];

	return await Promise.all([
		codeToHtml(code, {
			lang: language,
			theme: themes[0],
			transformers,
		}),
		codeToHtml(code, {
			lang: language,
			theme: themes[1],
			transformers,
		}),
	]);
}

export const CodeBlock = ({
	code,
	language,
	showLineNumbers = false,
	className,
	children,
	...props
}: CodeBlockProps) => {
	const themes = useCodeThemes();
	const [highlighted, setHighlighted] = useState<{
		code: string;
		language: BundledLanguage;
		showLineNumbers: boolean;
		themes: CodeThemePair;
		light: string;
		dark: string;
	} | null>(null);
	const current =
		highlighted?.code === code &&
		highlighted.language === language &&
		highlighted.showLineNumbers === showLineNumbers &&
		highlighted.themes === themes
			? highlighted
			: null;

	useEffect(() => {
		let cancelled = false;
		highlightCode(code, language, showLineNumbers, themes)
			.then(([light, dark]) => {
				if (!cancelled) {
					setHighlighted({ code, language, showLineNumbers, themes, light, dark });
				}
			})
			.catch(() => {
				// Keep the plain text visible if highlighting is unavailable.
			});

		return () => {
			cancelled = true;
		};
	}, [code, language, showLineNumbers, themes]);

	return (
		<CodeBlockContext.Provider value={{ code }}>
			<div
				className={cn(
					"group relative w-full overflow-hidden rounded-code border border-hairline bg-code-surface text-foreground",
					className,
				)}
				{...props}
			>
				<div className="relative">
					{current === null && (
						<pre className="m-0 overflow-auto bg-code-surface p-4 text-foreground text-sm">
							<code className="font-mono">{code}</code>
						</pre>
					)}
					<div
						className="overflow-auto dark:hidden [&>pre]:m-0 [&>pre]:bg-code-surface! [&>pre]:p-4 [&>pre]:text-foreground! [&>pre]:text-sm [&_code]:font-mono [&_code]:text-sm"
						// biome-ignore lint/security/noDangerouslySetInnerHtml: "this is needed."
						dangerouslySetInnerHTML={{ __html: current?.light ?? "" }}
					/>
					<div
						className="hidden overflow-auto dark:block [&>pre]:m-0 [&>pre]:bg-code-surface! [&>pre]:p-4 [&>pre]:text-foreground! [&>pre]:text-sm [&_code]:font-mono [&_code]:text-sm"
						// biome-ignore lint/security/noDangerouslySetInnerHtml: "this is needed."
						dangerouslySetInnerHTML={{ __html: current?.dark ?? "" }}
					/>
					{children && (
						<div className="absolute top-2 right-2 flex items-center gap-2">
							{children}
						</div>
					)}
				</div>
			</div>
		</CodeBlockContext.Provider>
	);
};

export type CodeBlockCopyButtonProps = ComponentProps<typeof Button> & {
	onCopy?: () => void;
	onError?: (error: Error) => void;
	timeout?: number;
};

export const CodeBlockCopyButton = ({
	onCopy,
	onError,
	timeout = 2000,
	children,
	className,
	...props
}: CodeBlockCopyButtonProps) => {
	const [isCopied, setIsCopied] = useState(false);
	const { code } = useContext(CodeBlockContext);

	const copyToClipboard = async () => {
		if (typeof window === "undefined" || !navigator?.clipboard?.writeText) {
			onError?.(new Error("Clipboard API not available"));
			return;
		}

		try {
			await navigator.clipboard.writeText(code);
			setIsCopied(true);
			onCopy?.();
			setTimeout(() => setIsCopied(false), timeout);
		} catch (error) {
			onError?.(error as Error);
		}
	};

	const Icon = isCopied ? CheckIcon : CopyIcon;

	return (
		<Button
			className={cn("shrink-0", className)}
			onClick={copyToClipboard}
			size="icon"
			variant="ghost"
			{...props}
		>
			{children ?? <Icon size={14} />}
		</Button>
	);
};
